// Core Vue imports
import { createApp } from 'vue';

import JsonViewer from 'vue-json-viewer';
import VueApexCharts from 'vue3-apexcharts';
// Store and Router
import { createPinia } from 'pinia';
// Vue Query
import { VueQueryPlugin } from '@tanstack/vue-query';

import * as Sentry from '@sentry/vue';
import { getCurrentUser } from 'aws-amplify/auth';
import mixpanel from 'mixpanel-browser';

// Composables
import { useAuth } from '@/composables/auth/useAuth';
// Configuration
import { loadSentry, logAppError } from '@/config/sentryConfig';
import { vueQueryOptions } from '@/config/vueQuery';
import sanitizeDirective from '@/directives/sanitize';
// Directives
import trackingDirective from '@/directives/tracking';
// Internationalization
import { useInternationalization } from '@/i18n';
import { toDayjsLocale } from '@/i18n/dayjsLocaleMap';
import { piniaResetPlugin } from '@/plugins/piniaReset';
import router from '@/router';
import { logout, LogoutReason } from '@/services/auth/logoutService';
import QuicksightEmbeddingContext from '@/services/quicksight/QuicksightEmbeddingContext';
// Environment
import { isProduction, isStage } from '@/utils/environment';
import { initIntercom } from '@/utils/intercomUtils';
import App from './App';
// Date and time handling
import dayjs from './plugins/dayjs';
// UI and styling
import vuetify from './plugins/vuetify';
import '@mdi/font/css/materialdesignicons.css';
import './styles/main.scss';

// These modules execute their code immediately when imported
import '@/config/cookieHub';
// Services
import '@/config/amplifyConfig';

// Initialize Mixpanel for session recordings only (event tracking moved to backend)
mixpanel.init(import.meta.env.VITE_MIXPANEL_ID, {
  debug: false,
  track_pageview: false,
  persistence: 'localStorage',
  // The number represents the percentage of the total sessions to send to mixpanel
  record_sessions_percent: isProduction ? 20 : 0
});

// Boot Intercom before any auth flow runs: the SDK buffers update() calls
// made after boot, but silently drops ones made before it. initApp() syncs
// the user identity during initializeApp() below — booting here is what
// makes that reliable on session restore. App.vue refines colors to the
// active Vuetify theme right after mount.
initIntercom();

// Create the Vue app instance
const app = createApp(App);
window.Backoffice = app;

// Set up Quicksight embedding
app.config.globalProperties.$quicksightEmbeddingContext =
  new QuicksightEmbeddingContext();

// Set up internationalization
const { i18n } = useInternationalization();
app.use(i18n);

// Align day.js with the initial i18n locale so the pre-bootstrap render window uses the
// right date formatting. setI18nLanguage() keeps them in sync from then on.
const initialLocale = i18n.global.locale.value;
const initialDayjsLocale = toDayjsLocale(initialLocale);
dayjs.locale(initialDayjsLocale);
dayjs.updateLocale(initialDayjsLocale, { weekStart: 1 }); // Monday as first day of week

// Make day.js available as a template global.
app.config.globalProperties.$dayjs = dayjs;

// Register directives
trackingDirective(app);
sanitizeDirective(app);

// Handle chunk loading errors with official Vite solution. The reload is
// rate-limited via sessionStorage so a persistent failure (e.g. assets host
// unreachable) degrades to the in-app error handling instead of an endless
// reload loop, while still allowing recovery from a later, unrelated release.
window.addEventListener('vite:preloadError', (event) => {
  const reloadKey = 'chunk-reload:vite-preload';
  try {
    const lastReloadMs = Number(sessionStorage.getItem(reloadKey));
    if (Number.isFinite(lastReloadMs) && Date.now() - lastReloadMs < 30_000) {
      return;
    }
    sessionStorage.setItem(reloadKey, String(Date.now()));
  } catch {
    // sessionStorage unavailable (storage disabled / strict privacy). Without it
    // we can't loop-protect, so don't reload — let Vite re-throw the preload error
    // and fall through to the i18n loader's in-app fallback instead of risking a
    // reload loop.
    return;
  }
  event.preventDefault();
  window.location.reload();
});

// Initialize Sentry for Stage and Prod
const isMainRelease = isProduction || isStage;

if (isMainRelease) {
  try {
    await loadSentry(app, router);
  } catch (e) {
    console.warn('Sentry failed to initialize', e);
  }
}

// Register UI components and core plugins
app.use(JsonViewer);
app.use(VueApexCharts);

// Setup Pinia with reset plugin
const pinia = createPinia();
pinia.use(piniaResetPlugin);
app.use(pinia);

// Tan Stack: Vue Query for data fetching and caching
app.use(VueQueryPlugin, vueQueryOptions);

app.use(vuetify);

/**
 * Initialize the application
 * This is the main entry point for the application
 */
const initializeApp = async () => {
  try {
    // Get auth functions from the composable
    const { initApp } = useAuth(app);

    // Try to get the current authenticated user
    let userExists = false;
    try {
      await getCurrentUser();
      userExists = true;
    } catch (userError) {
      console.log('No authenticated user found:', userError.message);
      // This is normal - user is just visiting the login page with no session
    }

    // If user exists, try to initialize the app
    if (userExists) {
      try {
        await initApp();
      } catch (initError) {
        console.log(
          'App initialization failed, forcing logout:',
          initError.message
        );

        logAppError({
          error: initError,
          functionName: 'initializeApp',
          section: 'initApp_failure',
          level: 'warning',
          message: 'App initialization failed for authenticated user'
        });

        // Guard against overwriting SessionExpired logout reason from interceptor
        const isSessionExpiredError =
          initError?.isSessionExpired === true ||
          initError?.response?.status === 401;
        if (!isSessionExpiredError) {
          await logout(app, LogoutReason.InitializationError);
        }
      }
    }
  } catch (error) {
    console.error('Application initialization failed:', error);

    // Send error to Sentry
    Sentry.withScope((scope) => {
      scope.setFingerprint(['app-initialization']);
      scope.setTag('section', 'app_initialization');
      scope.setLevel('fatal'); // Marks it with a red "Fatal" badge in the UI

      Sentry.captureException(error, {
        extra: {
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });
    });
  } finally {
    // Always register router and mount the app, regardless of auth state
    // This ensures the app is always responsive, even on initialization failure
    app.use(router);
    app.mount('#app');
  }
};

// Start the application initialization
initializeApp();
